import React from "react";

const DEFAULT_WIDTH = 800;
const DEFAULT_HEIGHT = 600;

function getDefaultColor(darkMode) {
	return darkMode ? "#fff" : "#111827";
}

function getElementColor(color, darkMode) {
	return color || getDefaultColor(darkMode);
}

function toAbsolute(value, size, fallback = 0) {
	if (typeof value !== "number" || Number.isNaN(value)) return fallback;
	if (value >= 0 && value <= 100) return (value / 100) * size;
	return value;
}

function buildTransform(element, x, y) {
	const transforms = [];
	const direction = element.direction;
	const rotation = typeof element.rotation === "number" ? element.rotation : null;

	if (direction === "up") transforms.push(`rotate(-90 ${x} ${y})`);
	if (direction === "down") transforms.push(`rotate(90 ${x} ${y})`);
	if (direction === "left") transforms.push(`rotate(180 ${x} ${y})`);
	if (rotation !== null) transforms.push(`rotate(${rotation} ${x} ${y})`);

	return transforms.length > 0 ? transforms.join(" ") : undefined;
}

function renderElement(element, index, width, height, markerId, darkMode) {
	if (!element || typeof element !== "object") return null;

	// Inline SVG element support
	if (element.type === "svg" && typeof element.svg === "string") {
		// Allow x, y, width, height, rotation, color
		const x = toAbsolute(element.x, width, width / 2);
		const y = toAbsolute(element.y, height, height / 2);
		const svgWidth = toAbsolute(element.width, width, width / 3);
		const svgHeight = toAbsolute(element.height, height, height / 3);
		const color = getElementColor(element.color, darkMode);
		const rotation = typeof element.rotation === "number" ? element.rotation : 0;
		// Wrap in a <g> for transform and color
		// Replace 'currentColor' in SVG string with color
		let svgString = element.svg;
		// Only replace if color is set or darkMode is used
		if (svgString.includes("currentColor")) {
			// color is applied via parent <g> color style
		}
		// eslint-disable-next-line react/no-danger
		return (
			<g
				key={element.id || `svg-${index}`}
				transform={`translate(${x},${y}) scale(${svgWidth / 100},${svgHeight / 100}) rotate(${rotation})`}
				style={svgString.includes("currentColor") ? { color } : undefined}
				opacity={element.opacity == null ? 1 : element.opacity}
			>
				<g dangerouslySetInnerHTML={{ __html: svgString }} />
			</g>
		);
	}

	switch (element.type) {
		case "text": {
			const x = toAbsolute(element.x, width, width / 2);
			const y = toAbsolute(element.y, height, height / 2);
			const fontSize = toAbsolute(element.fontSize, Math.min(width, height), 72);
			const transform = buildTransform(element, x, y);
			const fill = getElementColor(element.fill, darkMode);
			// If letterRotate is set, render each letter with its own rotation
			if (typeof element.letterRotate === "number" && element.text && element.text.length > 1) {
				// Calculate spacing between letters (approximate)
				const spacing = element.letterSpacing || 0;
				const chars = String(element.text).split("");
				// Start at center and offset each letter
				const totalWidth = chars.length * fontSize + (chars.length - 1) * spacing;
				const startX = x - totalWidth / 2 + fontSize / 2;
				return (
					<g key={element.id || `text-${index}`}
						opacity={element.opacity == null ? 1 : element.opacity}
						transform={transform}
					>
						{chars.map((ch, i) => {
							const charX = startX + i * (fontSize + spacing);
							return (
								<text
									key={i}
									x={charX}
									y={y}
									fill={fill}
									fontSize={fontSize}
									fontWeight={element.fontWeight || 700}
									fontFamily={element.fontFamily || "Arial, sans-serif"}
									textAnchor={element.anchor || "middle"}
									dominantBaseline={element.baseline || "middle"}
									letterSpacing={0}
									transform={`rotate(${element.letterRotate} ${charX} ${y})`}
								>
									{ch}
								</text>
							);
						})}
					</g>
				);
			}
			// Default: render as a single text element
			return (
				<text
					key={element.id || `text-${index}`}
					x={x}
					y={y}
					fill={fill}
					fontSize={fontSize}
					fontWeight={element.fontWeight || 700}
					fontFamily={element.fontFamily || "Arial, sans-serif"}
					textAnchor={element.anchor || "middle"}
					dominantBaseline={element.baseline || "middle"}
					letterSpacing={element.letterSpacing || 0}
					opacity={element.opacity == null ? 1 : element.opacity}
					transform={transform}
				>
					{element.text || ""}
				</text>
			);
		}
		case "line": {
			const stroke = getElementColor(element.stroke, darkMode);
			return (
				<line
					key={element.id || `line-${index}`}
					x1={toAbsolute(element.x1, width)}
					y1={toAbsolute(element.y1, height)}
					x2={toAbsolute(element.x2, width)}
					y2={toAbsolute(element.y2, height)}
					stroke={stroke}
					strokeWidth={toAbsolute(element.strokeWidth, Math.min(width, height), 6)}
					strokeLinecap={element.strokeLinecap || "round"}
					opacity={element.opacity == null ? 1 : element.opacity}
					strokeDasharray={Array.isArray(element.dash) ? element.dash.join(" ") : element.dash}
				/>
			);
		}
		case "arrow": {
			const stroke = getElementColor(element.stroke, darkMode);
			return (
				<line
					key={element.id || `arrow-${index}`}
					x1={toAbsolute(element.x1, width)}
					y1={toAbsolute(element.y1, height)}
					x2={toAbsolute(element.x2, width)}
					y2={toAbsolute(element.y2, height)}
					stroke={stroke}
					strokeWidth={toAbsolute(element.strokeWidth, Math.min(width, height), 6)}
					strokeLinecap={element.strokeLinecap || "round"}
					opacity={element.opacity == null ? 1 : element.opacity}
					markerEnd={`url(#${markerId})`}
				/>
			);
		}
		case "image": {
			const x = toAbsolute(element.x, width);
			const y = toAbsolute(element.y, height);
			const imageWidth = toAbsolute(element.width, width, width / 3);
			const imageHeight = toAbsolute(element.height, height, height / 3);
			const transform = buildTransform(element, x + imageWidth / 2, y + imageHeight / 2);
			return (
				<image
					key={element.id || `image-${index}`}
					href={element.src}
					x={x}
					y={y}
					width={imageWidth}
					height={imageHeight}
					preserveAspectRatio={element.preserveAspectRatio || "xMidYMid meet"}
					opacity={element.opacity == null ? 1 : element.opacity}
					transform={transform}
				/>
			);
		}
		default:
			return null;
	}
}

export default function RebusCardSvg({ card, className = "", title = "Rebus puzzle", darkMode }) {
	if (!card || typeof card !== "object") return null;

	const width = typeof card.width === "number" ? card.width : DEFAULT_WIDTH;
	const height = typeof card.height === "number" ? card.height : DEFAULT_HEIGHT;
	const markerId = `rebus-card-arrow-${String(card.id || "default")}`;

	return (
		<svg
			viewBox={`0 0 ${width} ${height}`}
			role="img"
			aria-label={title}
			className={className}
			xmlns="http://www.w3.org/2000/svg"
		>
			<title>{title}</title>
			<defs>
				<marker
					id={markerId}
					markerWidth="10"
					markerHeight="10"
					refX="8"
					refY="5"
					orient="auto"
					markerUnits="strokeWidth"
				>
					<path d="M0,0 L10,5 L0,10 z" fill="context-stroke" />
				</marker>
			</defs>
			<rect
				x="0"
				y="0"
				width={width}
				height={height}
				rx={card.cornerRadius || 24}
				ry={card.cornerRadius || 24}
				fill={card.background === undefined ? "none" : card.background}
			/>
			{(Array.isArray(card.elements) ? card.elements : []).map((element, index) =>
				renderElement(element, index, width, height, markerId, darkMode)
			)}
		</svg>
	);
}
